import type { Vec } from '@/lib/uploader/geometry'

import { BACKBOARD, ENTRY_Y, PANEL, RIM } from '@/lib/uploader/geometry'

import {
    clamp,
    easeOutCubic,
    hermite,
    lerp,
    popScale,
    random,
    smoothstep
} from '@/lib/uploader/math'

// Design pixels per second squared. Shared by the dotted aiming line and the real flight, so they always match.
export const GRAVITY = 2600

// The slingshot: pull the file back up to maxPull px; the further the pull, the faster the launch.
export const SLING = {
    maxPull: 150,
    minPull: 14,
    maxSpeed: 2050
} as const

// A flight that crosses the rim line within this many px of its centre is guided the rest of the way in.
const ASSIST = 24

export interface Pose {
    x: number
    y: number
    rotation: number
    scale: number
    opacity: number
    behind: boolean
}

export interface MotionEvent {
    at: number
    run: () => void
}

/*
 * A motion is a pure function of time plus a few timed events. Everything is planned at
 * release, so the flight, the bounce and the swish are deterministic and frame-rate free.
 */
export interface Motion {
    duration: number
    sample: (t: number) => Pose
    trail: [number, number] | null
    follow: [number, number] | null
    events: Array<MotionEvent>
}

export interface MakeHooks {
    onEntry: () => void
    onRelease: (push: number) => void
    onScore: () => void
    onRow: () => void
    onDone: () => void
}

export type MissKind = 'rim' | 'board'

export interface MissHooks {
    onContact: (kind: MissKind) => void
    onAirball: () => void
    onDone: () => void
}

export type SlingOutcome = 'make' | MissKind | 'air'

export interface SlingPlan {
    outcome: SlingOutcome
    time: number
    side: -1 | 1
    at: (t: number) => Vec
    velocityAt: (t: number) => Vec
}

const flightTime = (distance: number): number => clamp(0.7 + (distance - 150) / 350 * 0.2, 0.7, 0.9)

const parabolaAt = (from: Vec, velocity: Vec, t: number): Vec => ({
    x: from.x + velocity.x * t,
    y: from.y + velocity.y * t + 0.5 * GRAVITY * t * t
})

const launchVelocity = (from: Vec, to: Vec, time: number): Vec => ({
    x: (to.x - from.x) / time,
    y: (to.y - from.y) / time - 0.5 * GRAVITY * time
})

// Size falls off with distance from the viewer, so the shrink is quicker early in the flight.
const perspective = (start: number, end: number, progress: number): number =>
    start / (1 + (start / end - 1) * clamp(progress, 0, 1))

const inBackboard = (point: Vec): boolean =>
    point.x >= BACKBOARD.x - 8
    && point.x <= BACKBOARD.x + BACKBOARD.width + 8
    && point.y >= BACKBOARD.y - 8
    && point.y <= BACKBOARD.y + BACKBOARD.height

// The pull points away from the hoop; the launch goes the opposite way, faster the further it is pulled.
export const slingVelocity = (pull: Vec): Vec => {
    const length = Math.hypot(pull.x, pull.y)

    if (length === 0) return { x: 0, y: 0 }

    const speed = SLING.maxSpeed * Math.min(length, SLING.maxPull) / SLING.maxPull

    return { x: pull.x / length * speed, y: pull.y / length * speed }
}

/*
 * Follows the real parabola from the release point and decides what happens: it drops
 * through the rim (make), clips the end of the rim, hits the backboard, or sails past.
 * A near make is bent gently onto the rim centre, and the aiming line shows that same path.
 */
export const planSling = (from: Vec, velocity: Vec): SlingPlan => {
    const step = 1 / 240
    const limit = 1.4
    const natural = (t: number): Vec => parabolaAt(from, velocity, t)
    const naturalVelocity = (t: number): Vec => ({ x: velocity.x, y: velocity.y + GRAVITY * t })

    let crossing: { t: number, x: number } | null = null
    let boardAt: number | null = null
    let exitAt = limit
    let previous = from

    for (let t = step; t <= limit; t += step) {
        const point = natural(t)
        const falling = velocity.y + GRAVITY * t > 0

        if (falling && previous.y < ENTRY_Y && point.y >= ENTRY_Y) {
            const share = (ENTRY_Y - previous.y) / (point.y - previous.y)

            crossing = { t: t - step + share * step, x: previous.x + (point.x - previous.x) * share }

            break
        }

        if (boardAt === null && t > 0.2 && inBackboard(point)) boardAt = t

        if (point.y > PANEL.height + 60 || point.x < -90 || point.x > PANEL.width + 90) {
            exitAt = t

            break
        }

        previous = point
    }

    if (crossing) {
        const dx = crossing.x - RIM.cx
        const side: -1 | 1 = dx < 0 ? -1 : 1

        if (Math.abs(dx) <= ASSIST) {
            const time = crossing.t
            const correction = -dx

            return {
                outcome: 'make',
                time,
                side,
                at: t => {
                    const point = natural(t)

                    return { x: point.x + correction * Math.pow(t / time, 2), y: point.y }
                },
                velocityAt: t => {
                    const speed = naturalVelocity(t)

                    return { x: speed.x + 2 * correction * t / (time * time), y: speed.y }
                }
            }
        }

        if (Math.abs(dx) <= RIM.rx + 16) {
            return { outcome: 'rim', time: crossing.t, side, at: natural, velocityAt: naturalVelocity }
        }
    }

    const side: -1 | 1 = velocity.x < 0 ? -1 : 1

    if (boardAt !== null && (!crossing || boardAt < crossing.t)) {
        return { outcome: 'board', time: boardAt, side, at: natural, velocityAt: naturalVelocity }
    }

    return { outcome: 'air', time: Math.min(exitAt, 1.3), side, at: natural, velocityAt: naturalVelocity }
}

// Evenly spaced points along the planned flight, for the dotted aiming line.
export const previewPath = (plan: SlingPlan, spacing = 13): Array<Vec> => {
    const end = plan.outcome === 'air' ? Math.min(plan.time, 1.05) : plan.time
    const points: Array<Vec> = []
    let last = plan.at(0)
    let travelled = 0

    for (let t = 1 / 120; t <= end + 1e-6; t += 1 / 120) {
        const point = plan.at(Math.min(t, end))

        travelled += Math.hypot(point.x - last.x, point.y - last.y)
        last = point

        if (travelled >= spacing) {
            points.push(point)
            travelled = 0
        }
    }

    return points
}

interface Swish {
    length: number
    releaseAt: number
    push: number
    sample: (local: number) => Pose
}

/*
 * The swish: the net catches the file, holds it for a beat at the bottom, and lets it
 * slip out and fade. `local` is time since the file reached the rim.
 */
const swishPhase = (entry: Vec, arrival: Vec, residual: number): Swish => {
    const dropIn = clamp(arrival.y, 420, 1300)
    const catchY = RIM.cy + 30
    const holdY = RIM.cy + 66
    const slowIn = 2 * (catchY - entry.y) / (dropIn + 170)
    const hold = 0.2
    const slip = 0.26
    const releaseAt = slowIn + hold
    const push = clamp(arrival.x, -160, 160)

    return {
        length: Math.max(releaseAt + slip, 0.62),
        releaseAt,
        push,
        sample: local => {
            const fall = Math.max(0, local - releaseAt)
            let y: number

            if (local < slowIn) y = hermite(entry.y, catchY, dropIn, 170, slowIn, local)
            else if (local < releaseAt) y = hermite(catchY, holdY, 170, 60, hold, local - slowIn)
            else y = holdY + 60 * fall + 0.5 * 1900 * fall * fall

            return {
                x: local < 0.14 ? hermite(entry.x, RIM.cx, push, 0, 0.14, local) : RIM.cx,
                y,
                rotation: residual * (1 - easeOutCubic(local / 0.16)),
                scale: 0.62 - 0.04 * clamp(local / (releaseAt + slip), 0, 1),
                opacity: 1 - smoothstep(0.04, slip, fall),
                behind: true
            }
        }
    }
}

const swishEvents = (arrive: number, swish: Swish, hooks: MakeHooks): Array<MotionEvent> => [
    { at: arrive, run: hooks.onEntry },
    { at: arrive + swish.releaseAt, run: () => hooks.onRelease(swish.push) },
    { at: arrive + 0.42, run: hooks.onScore },
    { at: arrive + 0.6, run: hooks.onRow },
    { at: arrive + swish.length, run: hooks.onDone }
].sort((a, b) => a.at - b.at)

// After touching the rim or the backboard, the file pops off, tumbles the other way and fades.
const reboundPhase = (contact: Vec, bounce: Vec, rotation: number, spin: number, length: number) => (local: number): Pose => {
    const point = parabolaAt(contact, bounce, local)

    return {
        x: point.x,
        y: point.y,
        rotation: rotation - spin * 0.5 * local,
        scale: lerp(0.66, 0.82, easeOutCubic(local / length)),
        opacity: 1 - smoothstep(length * 0.5, length, local),
        behind: false
    }
}

// The slingshot flight the player aimed with the dotted line.
export const slingMotion = (launch: {
    from: Vec
    velocity: Vec
    rotation: number
    scale: number
    plan: SlingPlan
    make: MakeHooks
    miss: MissHooks
}): Motion => {
    const { from, velocity, rotation, scale, plan, make, miss } = launch
    const time = plan.time
    const direction: -1 | 1 = velocity.x >= 0 ? 1 : -1
    const residual = random(-6, 6)
    const makeSpin = direction * (time >= 0.5 ? 720 : 360) + residual - rotation
    const freeSpin = direction * 780

    const flight = (t: number): Pose => {
        const point = plan.at(t)
        const progress = time > 0 ? t / time : 1

        if (plan.outcome === 'make') {
            return {
                x: point.x,
                y: point.y,
                rotation: rotation + makeSpin * progress,
                scale: perspective(scale, 0.62, progress),
                opacity: 1,
                behind: progress > 0.6 && point.y > RIM.cy - 31
            }
        }

        return {
            x: point.x,
            y: point.y,
            rotation: rotation + freeSpin * t,
            scale: plan.outcome === 'air' ? perspective(scale, 0.72, t / 0.9) : perspective(scale, 0.66, progress),
            opacity: plan.outcome === 'air' ? 1 - smoothstep(time - 0.35, time, t) : 1,
            behind: false
        }
    }

    if (plan.outcome === 'make') {
        const swish = swishPhase(plan.at(time), plan.velocityAt(time), residual)

        return {
            duration: time + swish.length,
            sample: t => t < time ? flight(t) : swish.sample(t - time),
            trail: [0, time],
            follow: [time, time + swish.releaseAt],
            events: swishEvents(time, swish, make)
        }
    }

    if (plan.outcome === 'air') {
        return {
            duration: time,
            sample: flight,
            trail: [0, Math.max(0, time - 0.3)],
            follow: null,
            events: [
                { at: Math.min(time, Math.max(0.25, time - 0.5)), run: miss.onAirball },
                { at: time, run: miss.onDone }
            ]
        }
    }

    const kind = plan.outcome
    const shooter: -1 | 1 = from.x < RIM.cx ? -1 : 1
    const bounce = kind === 'board'
        ? { x: shooter * random(110, 170), y: random(-60, 20) }
        : { x: plan.side * random(110, 160) + shooter * 40, y: -random(250, 320) }
    const length = kind === 'board' ? 0.7 : 0.62
    const after = reboundPhase(plan.at(time), bounce, rotation + freeSpin * time, freeSpin, length)

    return {
        duration: time + length,
        sample: t => t < time ? flight(t) : after(t - time),
        trail: [0, time],
        follow: null,
        events: [
            { at: time, run: () => miss.onContact(kind) },
            { at: time + length, run: miss.onDone }
        ]
    }
}

/*
 * A planned made shot, used for real files and the keyboard: an optional pop-in where the
 * file appears, then a tumbling parabola that drops into the rim from above, then the swish.
 */
export const makeMotion = (launch: { from: Vec, rotation: number, scale: number, spawn: number, hooks: MakeHooks }): Motion => {
    const { from, rotation, scale, spawn, hooks } = launch
    const entry = { x: RIM.cx + random(-2.5, 2.5), y: ENTRY_Y }
    const time = flightTime(Math.hypot(entry.x - from.x, entry.y - from.y))
    const velocity = launchVelocity(from, entry, time)
    const arrival = { x: velocity.x, y: velocity.y + GRAVITY * time }
    const residual = random(-6, 6)
    const direction = Math.abs(entry.x - from.x) > 24 ? Math.sign(entry.x - from.x) : 1
    const spin = direction * 720 + residual - rotation
    const swish = swishPhase(entry, arrival, residual)
    const arrive = spawn + time

    const sample = (t: number): Pose => {
        if (t < spawn) {
            const progress = t / spawn

            return {
                x: from.x,
                y: from.y,
                rotation,
                scale: popScale(progress),
                opacity: clamp(progress * 1.8, 0, 1),
                behind: false
            }
        }

        if (t < arrive) {
            const local = t - spawn
            const point = parabolaAt(from, velocity, local)
            const progress = local / time

            return {
                x: point.x,
                y: point.y,
                rotation: rotation + spin * progress,
                scale: perspective(scale, 0.62, progress),
                opacity: 1,
                behind: progress > 0.6 && point.y > RIM.cy - 31
            }
        }

        return swish.sample(t - arrive)
    }

    return {
        duration: arrive + swish.length,
        sample,
        trail: [spawn, arrive],
        follow: [arrive, arrive + swish.releaseAt],
        events: swishEvents(arrive, swish, hooks)
    }
}

// Quietly steps aside, used when real files take the stage.
export const fadeMotion = (from: Vec, onDone: () => void): Motion => {
    const duration = 0.26

    return {
        duration,
        sample: t => ({
            x: from.x,
            y: from.y,
            rotation: 0,
            scale: lerp(1, 0.94, t / duration),
            opacity: 1 - t / duration,
            behind: false
        }),
        trail: null,
        follow: null,
        events: [{ at: duration, run: onDone }]
    }
}

// Reduced motion: no flight, no tumble. The file crossfades into the hoop instead.
export const ghostMotion = (hooks: MakeHooks, from: Vec | null): Motion => {
    const out = from ? 0.22 : 0

    return {
        duration: out + 0.85,
        sample: t => {
            if (from && t < out) {
                return {
                    x: from.x,
                    y: from.y,
                    rotation: 0,
                    scale: 1,
                    opacity: 1 - t / out,
                    behind: false
                }
            }

            const local = t - out

            return {
                x: RIM.cx,
                y: RIM.cy + 4,
                rotation: 0,
                scale: 0.6,
                opacity: smoothstep(0, 0.22, local) * (1 - smoothstep(0.5, 0.8, local)),
                behind: true
            }
        },
        trail: null,
        follow: null,
        events: [
            { at: out + 0.2, run: hooks.onEntry },
            { at: out + 0.45, run: hooks.onScore },
            { at: out + 0.6, run: hooks.onRow },
            { at: out + 0.85, run: hooks.onDone }
        ]
    }
}
